/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  EnhanceSpeechPollingException,
  EnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  fetchTrackStatus,
  NOT_FOUND_STATUS,
} from "components/EnhancedSpeech/Utilities";
import { mapFromServerFormat } from "utilities/ApiUtilities";
import { UseEnhanceDispatch } from "components/EnhancedSpeech/useEnhanceSpeech/Reducer";
import { useBuildEnhancement } from "components/EnhancedSpeech/useEnhanceSpeech/useBuildEnhancement";
import { useEffect, useMemo } from "react";
import { useErrorHandler } from "components/EnhancedSpeech/useEnhanceSpeech/useErrorHandler";
import {
  EnhancementObject,
  ProcessingState,
} from "components/EnhancedSpeech/useEnhanceSpeech";

export const useEnhanceSpeechPolling = (
  enhancements: EnhancementObject[],
  dispatch: UseEnhanceDispatch
): void => {
  const buildEnhancement = useBuildEnhancement(dispatch);
  const handleError = useErrorHandler(dispatch);

  const enhancementToPoll = useMemo((): EnhancementObject | undefined => {
    const unfinishedEnhancements = enhancements
      .filter((enhancement) => {
        return (
          enhancement.processingState !== ProcessingState.finished &&
          enhancement.processingState !== ProcessingState.uploading &&
          !enhancement.isDeleting
        );
      })
      .sort((a, b) => {
        // need to sort here because of multi upload:
        // files are added to the list in an arbitrary order on the frontend
        // but can be added to the DB in a different order depending on when
        // the files finish uploading.
        // We purposefully do not reorder the list after files finish uploading
        // to avoid jitter on the page. tracks may therefore process "out of order"
        // with respect to what the user sees, which is why we need this sort
        // here.
        const aCreatedAt = a.track.createdAt ?? "";
        const bCreatedAt = b.track.createdAt ?? "";
        return aCreatedAt.localeCompare(bCreatedAt);
      });
    if (unfinishedEnhancements.length > 0) {
      return unfinishedEnhancements[0];
    } else {
      return undefined;
    }
  }, [enhancements]);

  useEffect(() => {
    if (enhancementToPoll === undefined) {
      return;
    }

    const poll = async (): Promise<void> => {
      await performSinglePoll(
        enhancementToPoll,
        buildEnhancement,
        dispatch,
        handleError
      );
    };

    poll();
  }, [buildEnhancement, handleError, dispatch, enhancementToPoll]);
};

const performSinglePoll = async (
  enhancementToPoll: EnhancementObject,
  buildEnhancement: (track: EnhanceSpeechTrack) => EnhancementObject,
  dispatch: UseEnhanceDispatch,
  handleError: (track: EnhanceSpeechTrack) => void
): Promise<void> => {
  const averagePollIntervalMs = process.env.IS_TEST ? 1000 : 10_000; // Retry every 10 seconds
  const pollIntervalVarianceMs = process.env.IS_TEST ? 0 : 5_000;

  const jitter = (Math.random() * 2 - 1) * pollIntervalVarianceMs;
  const pollIntervalMs = Math.round(averagePollIntervalMs + jitter);

  // delay first because if enhancementToPoll just changed, we either just finished a poll,
  // an upload, or an initial page load.
  await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

  try {
    const pollResponse = await fetchTrackStatus(enhancementToPoll.track.id);

    if (pollResponse.ok) {
      const polledTrack: EnhanceSpeechTrack = mapFromServerFormat(
        await pollResponse.json()
      ) as EnhanceSpeechTrack;

      if (polledTrack.errorType) {
        handleError(polledTrack);
      } else {
        dispatch({
          type: "UPDATE_ENHANCEMENT",
          enhancement: buildEnhancement(polledTrack),
        });
      }
    } else {
      if (pollResponse.status === NOT_FOUND_STATUS) {
        // The track was deleted; stop polling and don't report
        dispatch({
          type: "REMOVE_ENHANCEMENT",
          trackId: enhancementToPoll.track.id,
        });
      } else {
        // Log and continue polling.
        // TODO: Should we continue polling?
        newrelic.noticeError(
          new Error("Error response while polling enhance quick action status"),
          {
            responseStatus: pollResponse.status,
            errorMessage: await pollResponse.text(),
          }
        );
        // Important to change ref here
        dispatch({
          type: "UPDATE_ENHANCEMENT",
          enhancement: buildEnhancement(enhancementToPoll.track),
        });
      }
    }
  } catch (error) {
    if (error && error instanceof EnhanceSpeechPollingException) {
      console.error(error); // Log but continue polling
      dispatch({
        type: "UPDATE_ENHANCEMENT",
        enhancement: buildEnhancement(enhancementToPoll.track),
      });
    } else {
      // unrecoverable error
      handleError({
        ...enhancementToPoll.track,
        errorType: EnhanceSpeechTrackErrorType.UNKNOWN_ERROR,
      });
    }
  }
};
